import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '@/api/http';
import { selectData, type SuccessResponseDTO } from '@/api/envelope';
import { usePagePermissionsStore } from '@/auth/page-permissions';
import { useAuthStore } from '@/auth/token-store';

export interface RolePageAccessEntry {
  roleCode: string;
  pageKey: string;
  allowed: boolean;
}

export interface RolePageAccessMatrix {
  pageKeys: string[];
  roleCodes: string[];
  entries: RolePageAccessEntry[];
}

const QUERY_KEY = ['system', 'role-permissions'] as const;

async function fetchMatrix(): Promise<RolePageAccessMatrix> {
  const res = await http.get<SuccessResponseDTO<RolePageAccessMatrix>>(
    '/admin/api/system/role-permissions',
  );
  return selectData(res.data);
}

async function putMatrix(entries: RolePageAccessEntry[]): Promise<RolePageAccessMatrix> {
  const res = await http.put<SuccessResponseDTO<RolePageAccessMatrix>>(
    '/admin/api/system/role-permissions',
    { entries },
  );
  return selectData(res.data);
}

export function useRolePermissionsMatrix() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchMatrix,
  });
}

export function useSaveRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: putMatrix,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
      const roles = useAuthStore.getState().session?.roles ?? [];
      await usePagePermissionsStore.getState().load(roles);
    },
  });
}

export const PAGE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  users: 'User Management',
  admins: 'Admin List',
  'system-permissions': 'System & Permissions',
  content: 'Content Management',
  moderation: 'Moderation',
  media: 'Media',
  'media-stickers': 'Media — Stickers',
  'media-transitions': 'Media — Transitions',
  'effect-packs': 'Effect Packs',
  effects: 'Effects',
  'tfx-create': 'TFX Create',
  'tfx-drafts': 'TFX Drafts',
  'tfx-review': 'TFX Review',
  'tfx-published': 'TFX Published',
  campaigns: 'Community Campaigns',
  finance: 'Financial Management',
  engagement: 'Engagement',
};

/** Labels for Entra App Roles shown on System & Permissions (no System Admin — maps to Administrator). */
export const ROLE_LABELS: Record<string, string> = {
  administrator: 'Administrator',
  content_manager: 'Content Manager',
  finance: 'Finance',
  marketer: 'Marketer',
  operator: 'Support Agent',
};
